/**
 * QR Code inline component — generates and displays QR codes with download/copy
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Download, Copy, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRCodeInline = ({ url, title, source }) => {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const getTrackedUrl = () => {
    let baseUrl = url;
    if (!baseUrl.startsWith('http')) {
      baseUrl = `${window.location.origin}${url}`;
    }
    const utmParams = new URLSearchParams({
      utm_source: 'qr_code',
      utm_medium: 'offline',
      utm_campaign: source || 'share'
    });
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${utmParams.toString()}`;
  };

  useEffect(() => {
    const generateQR = async () => {
      const trackedUrl = getTrackedUrl();
      try {
        const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(trackedUrl)}&format=png&margin=10`;
        const response = await fetch(qrApiUrl);
        const blob = await response.blob();
        const dataUrl = URL.createObjectURL(blob);
        setQrDataUrl(dataUrl);

        // Track QR generation
        try {
          await fetch(`${API_URL}/api/shares`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url, platform: 'qr_code', source: source || 'share' })
          });
        } catch {}
      } catch (err) {
        console.error('Failed to generate QR code:', err);
      } finally {
        setLoading(false);
      }
    };
    generateQR();
    return () => { if (qrDataUrl) URL.revokeObjectURL(qrDataUrl); };
  }, [url, source]);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${title || 'qr-code'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code downloaded!');
  };

  const handleCopy = async () => {
    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      toast.success('QR Code copied to clipboard!');
    } catch {
      toast.error('Failed to copy QR code');
    }
  };

  return (
    <div className="flex flex-col items-center py-4">
      {loading ? (
        <div className="w-[200px] h-[200px] bg-muted animate-pulse rounded-lg flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : qrDataUrl ? (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <img src={qrDataUrl} alt="QR Code" className="w-[200px] h-[200px]" />
        </div>
      ) : (
        <div className="w-[200px] h-[200px] bg-muted rounded-lg flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Failed to generate</p>
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={handleDownload} disabled={!qrDataUrl}>
          <Download className="w-4 h-4 mr-1" /> Download
        </Button>
        <Button variant="outline" size="sm" onClick={handleCopy} disabled={!qrDataUrl}>
          <Copy className="w-4 h-4 mr-1" /> Copy
        </Button>
      </div>
    </div>
  );
};

export default QRCodeInline;
